
/**
 * Module dependencies.
 */

import { IntersectionOptions, useInView } from 'react-intersection-observer';
import { ifProp, theme } from 'styled-tools';
import { kioskRoutes } from 'src/core/routes';
import { useRouter } from 'next/router';
import React, {
  ReactElement,
  ReactNode,
  useEffect,
  useMemo,
  useRef
} from 'react';

import Rellax from 'rellax';
import merge from 'lodash/merge';
import styled, { css } from 'styled-components';

/**
 * `ParallaxOptions` type.
 */

type ParallaxOptions = {
  percentage?: number,
  speed?: number,
  speedXs?: number
};

/**
 * `Props` type.
 */

type Props = {
  children?: ReactNode,
  className?: string,
  inViewOptions?: IntersectionOptions,
  parallaxOptions?: ParallaxOptions
};

/**
 * `ParallaxLayer` styled component.
 */

const ParallaxLayer = styled.div`
  height: auto;
`;

/**
 * `FadeInUpLayer` styled component.
 */

const FadeInUpLayer = styled.div<{ isVisible: boolean }>`
  height: 100%;
  transition:
    opacity 0.6s ease-out,
    transform 0.6s ${theme('animations.easeInOutSine')};
  will-change: opacity, transform;

  ${ifProp('isVisible', css`
    opacity: 1;
    transform: none;
  `, css`
    opacity: 0;
    transform: translateY(25%);
  `)}
`;

/**
 * `ParallaxFadeInUp` component.
 */

const ParallaxFadeInUp = (props: Props): ReactElement => {
  const { children, className, inViewOptions, parallaxOptions } = props;
  const router = useRouter();
  const isKiosk = useMemo(() => {
    return router.asPath.includes(kioskRoutes.home);
  }, [router]);

  const parallaxRef = useRef<HTMLDivElement>(null);
  const [ref, inView] = useInView(merge({}, {
    threshold: 0.2,
    triggerOnce: true
  }, inViewOptions));

  const parallaxProps = useMemo(() => {
    return isKiosk ? {} : {
      'data-rellax-percentage': parallaxOptions?.percentage ?? 0.5,
      'data-rellax-speed': parallaxOptions?.speed ?? 0,
      'data-rellax-xs-speed': parallaxOptions?.speedXs ?? 0
    };
  }, [isKiosk, parallaxOptions]);

  useEffect(() => {
    if (parallaxRef && parallaxRef.current && !isKiosk) {
      const instance = new Rellax(parallaxRef.current, {
        center: true
      });

      return () => {
        instance.destroy();
      };
    }
  }, [isKiosk]);

  return (
    <ParallaxLayer
      {...!isKiosk && {
        ref: parallaxRef,
        ...parallaxProps
      }}
      className={className}
    >
      <FadeInUpLayer
        {...!isKiosk && { ref }}
        isVisible={isKiosk ? true : inView}
      >
        {children}
      </FadeInUpLayer>
    </ParallaxLayer>
  );
};

/**
 * Export `ParallaxFadeInUp` component.
 */

export default ParallaxFadeInUp;
